import { ChangeEvent, useState, VFC } from "react";

// Components
import { FieldDate, FieldText, Flex, Panel, Spacer, Text, Title } from "components";
import { SearchByKillData, SearchByPassportNumber } from "../";

// Resources
import styles from "./BlockchainSearchForm.module.scss";

interface BlockchainSearchFormProps {
  onSearch: (params: SearchByPassportNumber | SearchByKillData) => void;
  isLoading: boolean;
}

export const BlockchainSearchForm: VFC<BlockchainSearchFormProps> = ({ onSearch, isLoading }) => {
  const [passportNumber, setPassportNumber] = useState<string>("");
  const [isPassportNumberDisabled, setIsPassportNumberDisabled] = useState<boolean>(false);

  const [killNumber, setKillNumber] = useState<string>("");
  const [killDate, setKillDate] = useState<Date | null>(null);
  const [isKillFieldsDisabled, setIsKillFieldsDisabled] = useState<boolean>(false);

  const handleUpdatePassportNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const newPassportNumber = event.target.value;

    if (passportNumber.length === 0 && newPassportNumber.length > 0) {
      setKillNumber("");
      setKillDate(null);
      setIsKillFieldsDisabled(true);
    } else if (passportNumber.length > 0 && newPassportNumber.length === 0) {
      setIsKillFieldsDisabled(false);
    }
    setPassportNumber(newPassportNumber);
  };

  const handleUpdateKillNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const newKillNumber = event.target.value;

    if (killNumber.length === 0 && newKillNumber.length > 0) {
      setPassportNumber("");
      setIsPassportNumberDisabled(true);
    } else if (killNumber.length > 0 && newKillNumber.length === 0 && !killDate) {
      setIsPassportNumberDisabled(false);
    }
    setKillNumber(newKillNumber);
  };

  const handleUpdateKillDate = (newKillDate: Date | null) => {
    if (!killDate && newKillDate) {
      setPassportNumber("");
      setIsPassportNumberDisabled(true);
    } else if (killDate && !newKillDate && killNumber.length === 0) {
      setIsPassportNumberDisabled(false);
    }
    setKillDate(newKillDate);
  };

  const handleSubmitSearch = () => {
    if (passportNumber !== "") {
      onSearch({ passportNumber, killNumber: null, killDate: null });
    } else if (killNumber !== "" && killDate) {
      onSearch({ passportNumber: null, killNumber, killDate });
    }
  };

  return (
    <>
      <Title secondary>Search the Blockchain</Title>

      <Spacer baselineHeight={1}></Spacer>

      <Panel
        actions={[
          {
            colour: "yellow",
            caption: `Find animal`,
            onClick: handleSubmitSearch,
            disabled: isLoading,
            requesting: isLoading,
          },
        ]}
      >
        <Flex container>
          <Flex item xs={3} className={styles.columnLeft}>
            <FieldText
              label="Passport ID:"
              inputProps={{
                name: "passportId",
                placeholder: "e.g. UK901091907912",
                disabled: isPassportNumberDisabled,
                value: passportNumber,
                onChange: handleUpdatePassportNumber,
              }}
            />
          </Flex>
        </Flex>

        <Spacer baselineHeight={4} border="middle"></Spacer>

        <Flex container containerJustifyContent="center">
          <Text smallest>Or search by kill details</Text>
        </Flex>

        <Spacer baselineHeight={2}></Spacer>

        <Flex container>
          <Flex item xs={3} className={styles.columnLeft}>
            <FieldText
              label="Kill Number:"
              inputProps={{
                name: "killNumber",
                placeholder: "e.g. 12415623",
                disabled: isKillFieldsDisabled,
                value: killNumber,
                onChange: handleUpdateKillNumber,
              }}
            />
          </Flex>

          <Flex item xs={3} className={styles.columnRight}>
            <FieldDate
              label="Kill Date:"
              inputProps={{
                name: "killDate",
                placeholder: "e.g. 20/02/2020",
                isClearable: true,
                disabled: isKillFieldsDisabled,
                value: killDate,
                onChange: handleUpdateKillDate,
              }}
            />
          </Flex>
        </Flex>
      </Panel>
    </>
  );
};
